import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import PageBase from '@components/pages/PageBase';
import Stepper from '@components/parts/Stepper';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Chip,
  Divider,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import { getEquipmentListApi } from '@apis/index';
import { EquipmentInfoDialogState, MessageDialogState } from '@atom/DialogState';
import { LoadingState } from '@atom/LoadingState';
import { FacilityState } from '@atom/FacilityState';

import { GetEquipmentListQueryVariables, getEquipmentListType } from '../../../API';
import { ReservationState } from '../../../atom/ReservationState';
import { IS_DISP_FEE } from '../../../config/applicationConfig';

// アコーディオン用のスタイル
const AccordionExpandUpper = styled(Accordion)(({ theme }) => ({
  display: 'flex',
  flexFlow: 'column-reverse nowrap'
}));
const CustomAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  alignItems: 'flex-start'
}));

type TabCategory = {
  category: string;
  categoryName: string;
};
const EquipmentRequestPage = () => {
  /* 共通State */
  const setMessageDialogState = useSetRecoilState(MessageDialogState);
  const setEquipmentInfoDialogState = useSetRecoilState(EquipmentInfoDialogState);
  const setLoading = useSetRecoilState(LoadingState);

  const [searchParams] = useSearchParams();

  // Recoilステート
  const [reservation, setReservation] = useRecoilState(ReservationState);
  const facility = useRecoilValue(FacilityState);
  const facilityId = searchParams.get('facilityId') ?? facility.facilityId;

  const navigate = useNavigate();
  const [value, setValue] = useState(0);

  const [equipmentList, setEquipmentList] = useState<getEquipmentListType[]>([]);

  // getEquipmentListType + number: numberの型を作成する
  const [selectedEquipmentList, setSelectedEquipmentList] = useState<any>(reservation.equipmentList);
  const [selectedEquipmentCount, setSelectedEquipmentCount] = useState<number>(reservation.selectedEquipmentCount);
  const [selectedEquipmentTotal, setSelectedEquipmentTotal] = useState<number>(reservation.selectedEquipmentTotal);
  const [totalFee, setTotalFee] = useState<number>(reservation.totalFee);

  const [tabs, setTabs] = useState<TabCategory[]>([]);

  const getData = async () => {
    setLoading(true);
    try {
      const input: GetEquipmentListQueryVariables = {
        input: {
          endTime: dayjs(reservation.reservedEndTime).format('HH:mm:ss'),
          facilityId,
          startTime: dayjs(reservation.reservedStartTime).format('HH:mm:ss'),
          targetDate: dayjs(reservation.reservedStartTime).format('YYYY-MM-DD')
        }
      };

      // データ取得
      const result = await getEquipmentListApi(input);

      // 備品の選択情報復元
      result.forEach((element: any) => {
        reservation.equipmentList?.some((equipment) => {
          if (equipment.equipmentId === element.equipmentId) {
            // eslint-disable-next-line no-param-reassign
            element.num = equipment.num;
            // eslint-disable-next-line no-param-reassign
            element.unitPrice = equipment.unitPrice;

            return true;
          }
          return false;
        });
      });

      setEquipmentList(result);

      // タブの一覧を作成
      const tabFilter = result.reduce((acc: any, item) => {
        // カテゴリをキーとして利用
        const key = item.category ?? '';

        // このキーが既に追加されていないか確認
        if (!acc.some((tab: any) => tab.category === key)) {
          // そのカテゴリがすでに存在しない場合のみ、新しいタブとして追加
          acc.push({
            category: key,
            categoryName: item.categoryName ?? ''
          });
        }
        return acc;
      }, []);

      setTabs(tabFilter);

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error(error);

      let message = '時間をおいてから再度お試しください。解決できない場合は管理者へお問い合わせください。';
      if ('errors' in error && error.errors.length !== 0) {
        message = error.errors[0].message;
      }
      setMessageDialogState({
        open: true,
        messageData: {
          title: 'エラーが発生しました。',
          message
        }
      });
    } finally {
      setLoading(false);
    }
  };
  const handleChange = (event: any, newValue: number) => {
    setValue(newValue);
  };

  const addCart = (item: any) => {
    const calEquipmentList = equipmentList.map((element: any) => {
      const result: any = { ...element };
      if (element.equipmentId === item.equipmentId) {
        result.num = item.itemNumber;
        result.unitPrice = item.unitPrice;
      }
      return result;
    });
    // EquipmentListの中身変更
    setEquipmentList(calEquipmentList);

    // 選択情報再設定
    setSelectedEquipmentList(calEquipmentList.filter((element: any) => element.num > 0));
  };

  useEffect(() => {
    // リロードされた場合は利用目的登録画面に戻す
    if (reservation.facilityId === '') {
      // クエリストリング取得
      const facilityIdParam = searchParams.get('facilityId') ?? '';
      const areaIdParam = searchParams.get('areaId') ?? '';
      const dateParam = searchParams.get('date') ?? '';
      const titleParam = searchParams.get('title') ?? '';
      const startTimeParam = searchParams.get('startTime') ?? '';
      const endTimeParam = searchParams.get('endTime') ?? '';
      const sectionListParam = searchParams.get('sectionList') ?? '';
      navigate(
        `/c005/purposeregister?facilityId=${facilityIdParam}&areaId=${areaIdParam}&date=${dateParam}&title=${titleParam}&startTime=${startTimeParam}&endTime=${endTimeParam}&sectionList=${sectionListParam}`
      );
    }else{
      getData();
    }
  }, []);

  const checkPrice = (element: any) => {
    // TODO 開始時間終了時間を受け取る
    const startTime = reservation.reservedStartTime;
    const endTime = reservation.reservedEndTime;
    const start = dayjs(startTime, 'HH:mm:ss');
    const end = dayjs(endTime, 'HH:mm:ss');

    const diff: number = end.diff(start, 'minute') / 60;
    let priceIndex;

    // 開始と終了時間の差で料金を設定する
    if (diff <= 4) {
      priceIndex = 0;
    } else if (diff >= 8) {
      priceIndex = 2;
    } else {
      priceIndex = 1;
    }

    // 規定単価
    const unitPrice = element.rentalPriceList[priceIndex].rentalPrice;
    const sum = unitPrice * element.num;

    return sum;
  };

  // 備品選択により再計算
  useEffect(() => {
    // 備品数
    let equipmentCount = 0;
    // 備品合計
    let equipmentTotal = 0;
    selectedEquipmentList.forEach((element: any) => {
      equipmentCount += element.num;

      const equipmentSum = checkPrice(element);
      // TODO:計算ロジック見直し
      equipmentTotal += equipmentSum;
    });

    setSelectedEquipmentCount(equipmentCount);
    setSelectedEquipmentTotal(equipmentTotal);
    const recalculationTotalFee = reservation.facilityFee + equipmentTotal;
    setTotalFee(recalculationTotalFee);

    // 予約情報設定
    setReservation({
      ...reservation,
      equipmentList: selectedEquipmentList,
      selectedEquipmentTotal: equipmentTotal,
      selectedEquipmentCount: equipmentCount,
      totalFee: recalculationTotalFee
    });
  }, [selectedEquipmentList]);

  const confirm = () => {
    navigate(
      `/c005/reservationconfirm?facilityId=${reservation.facilityId}&areaId=${reservation.areaId}&date=${dayjs(reservation.reservedStartTime).format('YYYY-M-D')}&title=${reservation.areaName}&startTime=${dayjs(reservation.reservedStartTime).format('HH:mm')}&endTime=${dayjs(reservation.reservedEndTime).format('HH:mm')}&sectionList=${reservation.sectionIdList}`
    );
  };

  const checkPriceIndex = () => {
    // TODO 開始時間終了時間を受け取る
    const startTime = reservation.reservedStartTime;
    const endTime = reservation.reservedEndTime;
    const start = dayjs(startTime, 'HH:mm:ss');
    const end = dayjs(endTime, 'HH:mm:ss');

    const diff: number = end.diff(start, 'minute') / 60;
    let priceIndex;

    // 開始と終了時間の差で料金を設定する
    if (diff <= 4) {
      priceIndex = 0;
    } else if (diff >= 8) {
      priceIndex = 2;
    } else {
      priceIndex = 1;
    }
    return priceIndex;
  };
  const priceIndex = checkPriceIndex();

  return (
    <PageBase title="備品申請" isButton={false}>
      {equipmentList ? (
        <Box sx={{ mr: 1, ml: 1 }}>
          <Stepper activeStep={1} />
          <Grid container spacing={2} sx={{ mt: '15px' }}>
            <Grid xs={12} sx={{ maxHeight: '95%' }}>
              <Tabs
                value={value}
                onChange={handleChange}
                indicatorColor="primary"
                textColor="primary"
                centered
                sx={{ width: '100%' }}
              >
                {tabs.map((label) => (
                  <Tab label={label.categoryName} />
                ))}
              </Tabs>
              {tabs.map((tab, index) => (
                <Box>
                  {index === value &&
                    equipmentList.map((element: any) => {
                      // 施設ID+エリアID+区画IDのリストを作成
                      const matchSections = reservation.sectionIdList.map(
                        (section) => `${reservation.facilityId}${reservation.areaId}${section}`
                      );

                      if (
                        // 表示する備品をフィルタリングするための条件を設定
                        index === 1
                          ? // 2つ目のタブは区画に紐づく設備を表示する
                            element.category === tab.category &&
                            matchSections.some((sectionCode) => element.equipmentId.startsWith(`E${sectionCode}`))
                          : // indexが1以外のときはエリアに紐づく備品及び施設共通の備品を表示
                            (element.category === tab.category &&
                              element.equipmentId.startsWith(`0${reservation.facilityId}${reservation.areaId}`)) ||
                            element.equipmentId.startsWith(`0${reservation.facilityId}000`)
                      ) {
                        return (
                          <Card
                            sx={{ display: 'flex', justifyContent: 'space-between' }}
                            onClick={() =>
                              setEquipmentInfoDialogState({
                                open: true,
                                equipmentInfoData: {
                                  equipmentId: element.equipmentId,
                                  facilityId,
                                  num: element.num,
                                  priceIndex
                                },
                                onAddCartClick: addCart
                              })
                            }
                          >
                            <Box sx={{ display: 'flex' }}>
                              <CardMedia
                                component="img"
                                sx={{ width: 80 }}
                                image={`/images/${facilityId}/equipment/${element.equipmentImagePath}`}
                              />
                              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <CardContent>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="h6">{element.equipmentName}</Typography>
                                    {element.num > 0 && (
                                      <Chip
                                        label={`${element.num}`}
                                        sx={{ width: '60px', backgroundColor: '#667080', color: '#FFFFFF' }}
                                      />
                                    )}
                                  </Box>
                                  {element.rentalPriceList?.map((price: any) => (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                      <Typography>{price.rentalPriceDivision}</Typography>
                                      <Typography sx={{ fontWeight: 'bold' }}>{price.rentalPrice}円</Typography>
                                    </Box>
                                  ))}
                                </CardContent>
                              </Box>
                            </Box>
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                textAlign: 'center'
                              }}
                            >
                              <ArrowForwardIosIcon />
                            </Box>
                          </Card>
                        );
                      }
                      return null;
                    })}
                </Box>
              ))}
            </Grid>
          </Grid>
          <Grid xs={12} sx={{ position: 'fixed', left: 0, bottom: 0 }}>
            <AccordionExpandUpper>
              <CustomAccordionSummary
                expandIcon={<ExpandLessIcon style={{ alignSelf: 'center', marginRight: 'auto' }} />}
              >
                <Grid container spacing={2}>
                  <Grid xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography>備品・設備</Typography>

                      <Typography>{selectedEquipmentCount}点</Typography>
                      {IS_DISP_FEE && (
                        <Typography>{selectedEquipmentTotal}円</Typography>
                      )}
                    </Box>
                  </Grid>
                  {IS_DISP_FEE && (
                    <Grid xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>総計</Typography>
                        <Typography variant="h6">{totalFee}円</Typography>
                      </Box>
                    </Grid>
                  )}
                  <Grid xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      {/* TODO 戻る場合は設定されるべきなのか確認 */}
                      <Button variant="outlined" fullWidth onClick={() => navigate(-1)}>
                        戻る
                      </Button>
                      <Button variant="contained" fullWidth onClick={() => confirm()}>
                        進む
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </CustomAccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  {IS_DISP_FEE && (
                    <Grid xs={12}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>施設利用料</Typography>
                        <Typography>{reservation.facilityFee}円</Typography>
                      </Box>
                    </Grid>
                  )}
                  <Grid xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography>備品・設備</Typography>
                    </Box>
                  </Grid>
                  <Grid xs={12}>
                    {selectedEquipmentList.map((element: any) => (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>{element.equipmentName}</Typography>
                        <Typography>{`${element.num}`}</Typography>
                        {/* TODO:計算ロジックを追加 */}
                        {IS_DISP_FEE && (
                          <Typography>{element.rentalPriceList[priceIndex].rentalPrice}円</Typography>
                        )}
                      </Box>
                    ))}
                  </Grid>
                  <Grid xs={12}>
                    <Divider />
                  </Grid>
                </Grid>
              </AccordionDetails>
            </AccordionExpandUpper>
          </Grid>
        </Box>
      ) : (
        ''
      )}
    </PageBase>
  );
};

export default EquipmentRequestPage;
